// Exports for the recurring-invoice lists and reports, and the printable
// invoice. Rows go out exactly as the screen filtered them.
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { cycleWord, fmtD, fmtDLong, lineOf, money2, r2 } from "./recurringModel";

const escHtml = (s) =>
  String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

export const PROFILE_HEAD = ["Profile no", "Customer", "Sales person", "Main service", "Invoice type", "Repeat", "Start", "End", "Next invoice", "Amount", "Status"];
export const profileCells = (p) => [
  p.no,
  p.custName,
  p.salesperson,
  p.service,
  p.type,
  cycleWord(p),
  p.start,
  p.end || "Never",
  p.next,
  r2(p.net),
  p.status,
];

export const INVOICE_HEAD = ["Invoice no", "Customer", "Sales person", "Main service", "Invoice type", "Invoice date", "Due date", "Taxable", "VAT", "Amount", "Payment status"];
export const invoiceCells = (x) => [x.no, x.custName, x.salesperson, x.service, x.type, x.date, x.due, r2(x.taxable), r2(x.vat), r2(x.net), x.pay];

const download = (blob, name) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
};

// kind: "csv" | "xlsx" | "pdf" | "print"
export const exportTable = (kind, name, title, head, rows) => {
  if (kind === "csv") {
    const q = (v) => '"' + String(v == null ? "" : v).replace(/"/g, '""') + '"';
    const lines = [head.map(q).join(",")].concat(rows.map((r) => r.map(q).join(",")));
    download(new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" }), name + ".csv");
    return;
  }
  if (kind === "xlsx") {
    const ws = XLSX.utils.aoa_to_sheet([head, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, name + ".xlsx");
    return;
  }
  if (kind === "pdf") {
    const doc = new jsPDF({ orientation: head.length > 8 ? "landscape" : "portrait" });
    doc.setFontSize(13);
    doc.text(title, 14, 14);
    autoTable(doc, { head: [head], body: rows, startY: 20, styles: { fontSize: 8 } });
    doc.save(name + ".pdf");
    return;
  }
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(
    "<html><head><title>" +
      escHtml(title) +
      "</title><style>body{font-family:Arial,sans-serif;font-size:12px}table{width:100%;border-collapse:collapse}th,td{border:1px solid #999;padding:5px 7px;text-align:left}th{background:#f2f2f2}</style></head><body><h2>" +
      escHtml(title) +
      "</h2><table><thead><tr>" +
      head.map((h) => "<th>" + escHtml(h) + "</th>").join("") +
      "</tr></thead><tbody>" +
      rows.map((r) => "<tr>" + r.map((c) => "<td>" + escHtml(c) + "</td>").join("") + "</tr>").join("") +
      "</tbody></table></body></html>"
  );
  w.document.close();
  w.print();
};

// one recurring invoice as a PDF (the pro-forma the customer pays against)
export const printInvoicePdf = (x, company, cur) => {
  const doc = new jsPDF();
  doc.setFontSize(15);
  doc.text(company || "Invoice", 14, 16);
  doc.setFontSize(10);
  doc.text("Recurring invoice " + x.no, 14, 24);
  doc.text("Date: " + fmtDLong(x.date) + "    Due: " + fmtDLong(x.due), 14, 30);
  doc.text("Bill to: " + (x.custName || "—"), 14, 36);
  doc.text((x.service || "") + (x.type ? " · " + x.type : "") + (x.profileNo ? " · " + x.profileNo : ""), 14, 42);
  autoTable(doc, {
    startY: 48,
    head: [["Item", "Qty", "Rate", "Disc %", "VAT %", "VAT", "Net"]],
    body: (x.items || []).map((l) => {
      const z = lineOf(l);
      return [
        l.productName + (l.note ? "\n" + l.note : ""),
        l.qty,
        money2(l.rate),
        l.disc ? l.disc + "%" : "—",
        l.taxable ? l.vatPct + "%" : "Exempt",
        money2(z.vat),
        money2(z.net),
      ];
    }),
    styles: { fontSize: 9 },
    columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" } },
  });
  const y = (doc.lastAutoTable ? doc.lastAutoTable.finalY : 60) + 8;
  doc.text("Taxable total: " + money2(x.taxable), 140, y);
  doc.text("VAT total: " + money2(x.vat), 140, y + 6);
  doc.setFontSize(12);
  doc.text("Net total: " + (cur ? cur + " " : "") + money2(x.net), 140, y + 14);
  doc.setFontSize(9);
  doc.text("Status: " + x.pay + (x.pay === "Paid" && x.paidOn ? " on " + fmtD(x.paidOn) : ""), 14, y);
  doc.save(x.no + ".pdf");
};
